import {Buffer} from "buffer";
import GtfsRealtimeBindings from "gtfs-realtime-bindings";

const GtfsRealtime = (() => {
	'use strict';

	let map,
		realtimeMarkers = [],
		visibleTrips,
		visibleStop,
		intervalId,
		pinSvg,
		$dropdownDestination = document.getElementById( 'tm__destination' ),
		$nextArrival = document.getElementById( 'tm__stop' ).querySelector( ".tm__next-arrival" ),
		$nextArrivalMinutes = $nextArrival.querySelector( "strong" );
	const $sidebar = document.getElementById( 'tm__sidebar' );

	const constructor = () => {
		map = TransitMap.getProperty( 'map' );

		$sidebar.addEventListener( 'tm__change-panel', ( e ) => {
			let pane = e.detail.pane.split( '|' ),
				paneId = parseInt( pane[1], 10 );
			switchToPanel( e.detail.panel, paneId );
		} );

		// immediately update/hide markers when changing tabs (instead of waiting up to 3 seconds for the automatic update)
		$sidebar.addEventListener( 'tm__change-tab', () => {
			continualGtfsUpdate();
		} );

		$dropdownDestination.addEventListener( "change", () => {
			$nextArrival.hidden = true;
			fetchTripUpdates();
		} );

		// need to init the realtime data specially if we're loading the map page directly into a route or stop
		maybeInitRealtimeOnLoad();

		// custom real-time bus pins
		const parser = new DOMParser(),
			svg = '<svg width="45" height="60" xmlns="http://www.w3.org/2000/svg"><path fill="#fff" d="M7.4 7.4h30.3v30.2H7.4z"/><path d="M14.2 27a2 2 0 0 0-1.8 2 1.9 1.9 0 1 0 1.8-2zm17.6-12a2 2 0 0 0-2-2H15.2a2 2 0 0 0-1.9 2v7.5c0 1.1 1 1.9 2 1.9H30a2 2 0 0 0 1.9-2zm-1 12c-1 0-1.8 1-1.8 2a1.9 1.9 0 1 0 1.8-2zM0 22.5c0 9 3.2 11.6 20.2 36.3 1.2 1.6 3.5 1.6 4.6 0C41.8 34 45 31.6 45 22.5a22.5 22.5 0 1 0-45 0zm36 0h-.6v9.7c0 .6-.6 1.4-1.5 1.4h-.4v1.9c0 1-.7 1.7-1.9 1.7H30c-1 0-2-.7-2-1.7v-2H17v2c0 1-.8 1.7-1.8 1.7h-2c-1 0-1.8-.7-1.8-1.7v-2c-1 0-1.8-.7-1.8-1.7v-9.3h-.2c-.9 0-1.5-.6-1.5-1.4v-4.7c0-.7.6-1.3 1.5-1.3h.4v-2.8c0-2.5 5.7-4.6 13-4.6s12.8 2 12.8 4.6V15h.6c.6 0 1.2.6 1.2 1.3V21c0 .8-.7 1.4-1.4 1.4z" fill="#212121"/></svg>';
		pinSvg = parser.parseFromString( svg, 'image/svg+xml' ).documentElement;
	}

	const switchToPanel = ( panel, paneId ) => {
		switch ( panel ) {
			case "stop_detail":
				visibleStop = paneId;
				$nextArrival.hidden = true;
			// note: deliberately not breaking here, include everything from the route_detail case
			case "route_detail":
				updateVisibleTrips( paneId );
				initContinualGtfsUpdates();
				break;
			case "overview_bus_routes":
			case "agency_detail":
				clearInterval( intervalId );
				intervalId = null;
				visibleTrips = null;
				clearVehicleMarkers();
				break;
		}
	}

	const maybeInitRealtimeOnLoad = () => {
		const params = new URLSearchParams( location.search ),
			tab = params.get( "tab" ) ? params.get( "tab" ) : "bus_routes";

		if ( tab !== 'bus_routes' ) {
			return;
		}

		const agencyId = params.get( "agency" ) ? parseInt( params.get( "agency" ), 10 ) : null,
			routeId = params.get( "route" ) ? parseInt( params.get( "route" ), 10 ) : null,
			stopId = params.get( "stop" ) ? parseInt( params.get( "stop" ), 10 ) : null;

		if ( !agencyId || !routeId ) {
			return;
		}

		const panel = stopId ? 'stop_detail' : 'route_detail',
			pane = stopId ? stopId : routeId;

		if ( panel === 'stop_detail' ) {
			updateVisibleTrips( routeId );
		}

		switchToPanel( panel, pane );
	}

	const initContinualGtfsUpdates = () => {
		if ( intervalId ) return;
		continualGtfsUpdate();
		intervalId = setInterval( () => {
			continualGtfsUpdate()
		}, 3000 );
	}

	const continualGtfsUpdate = () => {
		if ( history.state && (history.state.bus_routes.panel === 'stop_detail' || history.state.bus_routes.panel === 'route_detail') ) {
			if ( history.state.tab === 'trip_planner' && $sidebar.dataset.hasDirectionsResults === 'true' ) {
				// if on the trip planner with visible directions, don't update, but keep checking to see if we might update again in future
				clearVehicleMarkers();
				return;
			}

			fetchTripUpdates();
			fetchVehiclePositions();
			return;
		}

		// if no longer on the route or stop detail panel, stop checking for updates
		clearInterval( intervalId );
		intervalId = null;
	}

	const getFeed = ( type ) => {
		const visible = TransitMap.getProperty( 'visible' );
		if ( gtfsRt[visible.agencyId] && gtfsRt[visible.agencyId][type] )
			return gtfsRt[visible.agencyId][type];
		return false;
	}

	const fetchTripUpdates = () => {
		if ( !visibleTrips ) return;

		const url = getFeed( 'trips' );
		if ( !url ) return;

		fetch( url )
			.then( response => response.arrayBuffer() )
			.then( arrayBuffer => {
				const buffer = Buffer.from( arrayBuffer );
				const feedMessage = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode( buffer );
				updateStopArrivalTimes( feedMessage );
			} )
			.catch( err => {
				console.warn( 'Error:', err );
			} );
	}

	const updateStopArrivalTimes = ( feedMessage ) => {
		if ( !feedMessage ) return;

		const destinationSelected = parseInt( $dropdownDestination.value, 10 );

		let nextArrival = {
			seconds: null
		};

		// loop through all the trips and find which (if any) will visit this stop in the least amount of time
		feedMessage.entity.forEach( entity => {
			if ( !visibleTrips.includes( entity.tripUpdate.trip.tripId ) || entity.tripUpdate.trip.directionId !== destinationSelected ) {
				// wrong route or wrong direction
				return;
			}

			for ( const stop of entity.tripUpdate.stopTimeUpdate ) {
				if ( parseInt( stop.stopId, 10 ) !== visibleStop ) {
					// not the stop we're looking for
					continue;
				}

				// save this vehicle, unless it arrives after the already-saved vehicle
				const arrivalSeconds = stop.arrival.time - Date.now() / 1000;
				if ( !nextArrival.seconds || nextArrival.seconds > arrivalSeconds ) {
					nextArrival.seconds = arrivalSeconds;
				}
			}
		} );

		if ( nextArrival.seconds ) {
			// update the "Next Arrival" display
			const hours = Math.floor( nextArrival.seconds / 3600 ),
				minutes = Math.floor( (nextArrival.seconds % 3600) / 60 ),
				hoursStr = hours > 0 ? hours + '&nbsp;' + (hours > 1 ? tmVars.translations.hrs : tmVars.translations.hr) + ' ' : '',
				minutesStr = minutes + '&nbsp;' + tmVars.translations.min;
			$nextArrivalMinutes.innerHTML = hoursStr + minutesStr;
			$nextArrival.hidden = false;
		}
	}

	const fetchVehiclePositions = () => {
		if ( !visibleTrips ) return;

		const url = getFeed( 'pos' );
		if ( !url ) return;

		fetch( url )
			.then( response => response.arrayBuffer() )
			.then( arrayBuffer => {
				const buffer = Buffer.from( arrayBuffer );
				const feedMessage = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode( buffer );
				updateVehiclePositionMarkers( feedMessage );
			} )
			.catch( err => {
				console.error( 'Error:', err );
			} );
	}

	const updateVehiclePositionMarkers = ( feedMessage ) => {
		if ( !feedMessage || !visibleTrips ) return;

		clearVehicleMarkers();

		feedMessage.entity.forEach( entity => {
			if ( !visibleTrips.includes( entity.vehicle.trip.tripId ) || !entity.vehicle.position ) {
				// wrong route, or no position data
				return;
			}

			const marker = new google.maps.marker.AdvancedMarkerElement( {
				map: map,
				position: {lat: entity.vehicle.position.latitude, lng: entity.vehicle.position.longitude},
				content: pinSvg
			} );

			realtimeMarkers.push( marker );
		} );
	}

	const updateVisibleTrips = ( routeId ) => {
		let tripIds = tmTrips[routeId];
		if ( !tripIds ) return null;
		visibleTrips = tripIds.split( ',' );
	}

	const clearVehicleMarkers = () => {
		for ( const marker of realtimeMarkers ) {
			marker.setMap( null );
		}
		realtimeMarkers = [];
	}

	return {
		init: () => {
			let $map = document.getElementById( 'tm__map' );
			if ( !$map ) return;

			if ( $map.dataset.loaded ) {
				constructor();
			} else {
				window.addEventListener( "tm__gmaps-loaded", constructor );
			}
		}
	}
})();

GtfsRealtime.init()
